/**
 * People Desk previews (2026-10-07, Manak approved hr-dashboard-preview-mockup):
 * two compact boxes in the right column, under Hiring. The full table, filters
 * and Excel live on /hr/app-tour-progress; every app card on /hr/mobile-apps.
 */
import { Download } from 'lucide-react';
import { DeskSection, DeskState } from './ProfileDeskParts';
import { ForcedTag } from '@/pages/hr/MobileAppsPage';
import { ProgressBar } from '@/pages/hr/AppTourProgressPage';
import { lowestFirst, tourProgressRows, tourTotals, useMobileApps, useTourProgress } from '@/pages/hr/appOverviewData';

export const MOBILE_APPS_PATH = '/hr/mobile-apps';
export const TOUR_PROGRESS_PATH = '/hr/app-tour-progress';

export function HrTourPreview() {
  const progress = useTourProgress();
  const rows = progress.data ? tourProgressRows(progress.data, null) : null;
  const totals = rows ? tourTotals(rows) : null;
  return <DeskSection title="App Tour progress" more={{ to: TOUR_PROGRESS_PATH, label: 'View all →' }}>
    <DeskState resource={progress} />
    {rows && totals && <>
      <div aria-label="App Tour totals" className="grid grid-cols-4 gap-2 text-center">
        {([['Staff', totals.employees], ['Started', totals.started], ['Finished', totals.finished], ['Not started', totals.notStarted]] as const).map(([label, n]) =>
          <div key={label} className="rounded border border-gray-200 p-2"><strong className="block text-xl">{n}</strong><span className="text-xs text-gray-600">{label}</span></div>)}
      </div>
      {rows.length > 0 && <>
        <p className="mt-4 mb-2 text-sm text-gray-600">Lowest progress</p>
        <ul aria-label="Lowest progress" className="space-y-2">
          {lowestFirst(rows).slice(0, 5).map((r) => <li key={r.userId} className="flex items-center justify-between gap-2 text-sm"><span className="min-w-0 truncate">{r.name}</span><ProgressBar pct={r.pct} /></li>)}
        </ul>
      </>}
    </>}
  </DeskSection>;
}

export function HrAppsPreview() {
  const apps = useMobileApps();
  return <DeskSection title="Mobile apps" more={{ to: MOBILE_APPS_PATH, label: 'All apps →' }}>
    <DeskState resource={apps} />
    {apps.data && (apps.data.length === 0 ? <p>No app releases are published yet.</p> :
      <ul className="space-y-2">
        {apps.data.map((a) => <li key={a.key} className="flex items-center justify-between gap-2 text-sm">
          <span className="min-w-0"><strong className="font-medium">{a.name}</strong> <span className="text-gray-600">{a.versionName}</span> {a.forced && <ForcedTag />}</span>
          <a href={a.apkUrl} download aria-label={`Download ${a.name}`} className="inline-flex shrink-0 items-center gap-1 rounded border px-2 py-1"><Download size={14} /> Download</a>
        </li>)}
      </ul>)}
  </DeskSection>;
}
